/**
 * File-type detection by magic bytes. Shared by the browser
 * (instant rejection) and the upload function (authoritative check).
 * Never trust the extension or the declared MIME type alone.
 */

export type ArtworkFormat = 'png' | 'jpeg' | 'svg' | 'pdf';

export const ARTWORK_MAX_BYTES = 25 * 1024 * 1024;

export const ARTWORK_MIME: Record<ArtworkFormat, string> = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
};

export function sniffFormat(head: Uint8Array): ArtworkFormat | null {
  if (head.length >= 8 && head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47) return 'png';
  if (head.length >= 3 && head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return 'jpeg';
  // %PDF may be preceded by a few junk bytes in the wild; allow up to 1 KB.
  const ascii = asciiPrefix(head, 1024);
  const pdfAt = ascii.indexOf('%PDF-');
  if (pdfAt >= 0 && pdfAt < 1024) return 'pdf';
  const text = ascii.replace(/^﻿/, '').trimStart().toLowerCase();
  if ((text.startsWith('<?xml') || text.startsWith('<svg') || text.startsWith('<!--') || text.startsWith('<!doctype svg')) && text.includes('<svg')) return 'svg';
  return null;
}

function asciiPrefix(bytes: Uint8Array, n: number): string {
  let s = '';
  const len = Math.min(bytes.length, n);
  for (let i = 0; i < len; i++) s += String.fromCharCode(bytes[i]);
  return s;
}

/** Server-side guard: reject SVGs that carry active content instead of trying to clean them. */
export function svgHasActiveContent(svgText: string): boolean {
  const t = svgText.toLowerCase();
  return (
    /<script[\s>]/.test(t) ||
    /<foreignobject[\s>]/.test(t) ||
    /\son[a-z]+\s*=/.test(t) ||
    /javascript:/.test(t) ||
    /<(iframe|embed|object)[\s>]/.test(t)
  );
}
